import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatRupiah, methodLabel } from "@/lib/billing";
import { CheckCircle2, AlertTriangle, Loader2, Search, X } from "lucide-react";

const PAGE = 200;

// Drill-down read-only: daftar pembayaran sumber KPI + rekonsiliasi dgn nilai kartu.
export function PaymentDrillDialog({ open, onOpenChange, title, description, params, kpiValue }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [sum, setSum] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [scopeSum, setScopeSum] = useState(0);
  const [scopeTotal, setScopeTotal] = useState(0);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 400);
    return () => clearTimeout(t);
  }, [qInput]);

  const fetchPage = async (p) => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/payments", {
        params: { ...params, ...(q ? { q } : {}), page: p, limit: PAGE },
      });
      setRows((prev) => (p === 1 ? data.items || [] : [...prev, ...(data.items || [])]));
      setTotal(data.total || 0);
      setSum(data.sum_amount_received || 0);
      setScopeSum(data.scope_sum_amount_received ?? data.sum_amount_received ?? 0);
      setScopeTotal(data.scope_total ?? data.total ?? 0);
      setPage(p);
    } catch (e) {
      toast.error("Gagal memuat transaksi: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { setRows([]); fetchPage(1); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, JSON.stringify(params), q]);

  useEffect(() => { if (!open) { setQInput(""); setQ(""); } }, [open]);

  // Rekonsiliasi selalu atas seluruh cakupan filter, bukan hasil cari.
  const match = !loading && scopeSum === kpiValue;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[calc(100vw-1.5rem)] max-h-[85vh] flex flex-col" data-testid="payment-drill-dialog">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg">{title}</DialogTitle>
          <p className="text-xs text-[#6B7280]">{description}</p>
        </DialogHeader>

        <div className={`rounded-md border px-3 py-2 text-xs flex flex-wrap items-center gap-x-4 gap-y-1 ${match ? "bg-[#ECFDF5] border-[#A7F3D0]" : "bg-[#FFFBEB] border-[#FDE68A]"}`}
             data-testid="payment-drill-reconcile">
          <span>Transaksi: <b className="font-mono" data-testid="payment-drill-count">{scopeTotal}</b></span>
          <span>Σ nominal: <b className="font-mono" data-testid="payment-drill-sum">{formatRupiah(scopeSum)}</b></span>
          <span>Nilai kartu: <b className="font-mono">{formatRupiah(kpiValue)}</b></span>
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : match ? (
            <span className="inline-flex items-center gap-1 font-semibold text-[#047857]"><CheckCircle2 className="h-3.5 w-3.5" /> Cocok</span>
          ) : (
            <span className="inline-flex items-center gap-1 font-semibold text-[#B45309]" data-testid="payment-drill-mismatch"><AlertTriangle className="h-3.5 w-3.5" /> Selisih {formatRupiah(kpiValue - scopeSum)}</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[12rem]">
            <Search className="h-3.5 w-3.5 text-[#9CA3AF] absolute left-2 top-1/2 -translate-y-1/2" />
            <input
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Cari ID pembayaran, customer, kolektor, metode, invoice, periode…"
              maxLength={100}
              className="w-full h-8 pl-7 pr-7 rounded-md border border-[#E5E7EB] text-xs focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/30"
              data-testid="payment-drill-search"
            />
            {qInput && (
              <button type="button" onClick={() => setQInput("")} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#374151]"
                      aria-label="Hapus pencarian" data-testid="payment-drill-search-clear">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {q && (
            <span className="text-xs text-[#1E3A8A] bg-[#EFF6FF] border border-[#BFDBFE] rounded-md px-2 py-1" data-testid="payment-drill-search-result">
              Hasil cari: <b className="font-mono">{total}</b> transaksi · <b className="font-mono">{formatRupiah(sum)}</b>
            </span>
          )}
        </div>

        <div className="overflow-auto border rounded-md flex-1 min-h-0">
          <table className="w-full min-w-[56rem] text-xs">
            <thead className="bg-[#F9FAFB] text-[#6B7280] sticky top-0">
              <tr>
                <th className="text-left px-2 py-1.5 font-semibold">ID Pembayaran</th>
                <th className="text-left px-2 py-1.5 font-semibold">Tgl Bayar</th>
                <th className="text-left px-2 py-1.5 font-semibold">Nama Customer</th>
                <th className="text-left px-2 py-1.5 font-semibold" title="Diterima Oleh (snapshot saat pembayaran dicatat)">Nama Kolektor</th>
                <th className="text-right px-2 py-1.5 font-semibold">Nominal</th>
                <th className="text-left px-2 py-1.5 font-semibold">Metode</th>
                <th className="text-left px-2 py-1.5 font-semibold">Invoice</th>
                <th className="text-left px-2 py-1.5 font-semibold">Periode Tagihan</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.payment_id} className="border-t" data-testid={`payment-drill-row-${r.payment_id}`}>
                  <td className="px-2 py-1.5 font-mono whitespace-nowrap">{r.payment_id}</td>
                  <td className="px-2 py-1.5 font-mono whitespace-nowrap">{r.payment_date}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap max-w-[14rem] truncate" title={r.customer_name || ""} data-testid={`payment-drill-customer-${r.payment_id}`}>{r.customer_name || "—"}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap" data-testid={`payment-drill-collector-${r.payment_id}`}>
                    {r.collector_name || "—"}
                    {r.collector_name && r.received_role ? <span className="ml-1 text-[10px] text-[#9CA3AF]">({r.received_role})</span> : null}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-right whitespace-nowrap">{formatRupiah(r.amount_received || 0)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{methodLabel ? methodLabel(r.method) : r.method}</td>
                  <td className="px-2 py-1.5 font-mono whitespace-nowrap">{r.invoice_id}</td>
                  <td className="px-2 py-1.5 font-mono whitespace-nowrap">{r.invoice_periode_tagihan || "—"}</td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-2 py-6 text-[#6B7280]">
                  <span className="sticky left-2" data-testid="payment-drill-empty">{q ? "Tidak ada transaksi yang cocok dengan pencarian." : "Tidak ada transaksi pada filter ini."}</span>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {rows.length < total && (
          <button type="button" onClick={() => fetchPage(page + 1)} disabled={loading}
                  className="self-center text-xs font-semibold text-[#1D4ED8] hover:underline disabled:opacity-50"
                  data-testid="payment-drill-more">
            Muat lebih banyak ({rows.length}/{total})
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
